import { ref } from 'vue'
import type { FavoriteStore, SyncResponse } from '~/interfaces/favorites'
import { FavoritesSyncSuccessSchema, type FavoritesSyncSuccess } from '~/services/api/generated'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { resolveProblemMessageWithReference } from '~/services/api/resolveProblemMessageWithReference'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useLogger } from '~/composables/useLogger'
import { identityGeneration } from '~/utils/identityGeneration'
import { Normalization } from '~/utils/normalization'
import { applyFavoriteStoreChange } from '~/utils/applyFavoriteStoreChange'
import { getFavoriteStoreIds } from '~/utils/getFavoriteStoreIds'
import { normalizeFavoriteStoreId } from '~/utils/normalizeFavoriteStoreId'
import { toggleFavoriteId } from '~/utils/toggleFavoriteId'
import { useGuestFavoritesCookie } from '~/composables/useGuestFavoritesCookie'
import { useSearchApplication } from '~/composables/useSearchApplication'

const logger = useLogger('useFavorites')

export const useFavorites = () => {
  const { isAuthenticated } = useIdentity()
  const sanctumClient = useApiRequest()
  const { searchStore, fetchFavoriteStores } = useSearchApplication()
  const { guestFavoritesCookie, isValidGuestCookie, writeGuestFavorites, resetGuestFavorites } =
    useGuestFavoritesCookie()
  const t = usePresentationTranslator()

  const isSyncing = ref(false)

  // Get current favorites (cookie or local store state)
  const getCurrentFavorites = (): number[] => {
    if (isAuthenticated.value) {
      // Authenticated user: use favorites from store/DB
      return getFavoriteStoreIds(searchStore.favoriteStores)
    }

    // Guest: use cookie only if it belongs to current guest
    if (isValidGuestCookie()) {
      return Normalization.intList(guestFavoritesCookie.value.favorites)
    }

    // Cookie belongs to another guest, return empty
    return []
  }

  const syncFavoritesFromCookie = (): void => {
    if (searchStore.isSyncingFavorites) return

    try {
      searchStore.isSyncingFavorites = true
      if (isAuthenticated.value) return

      const guestFavorites = getCurrentFavorites()
      if (guestFavorites.length === 0) return

      searchStore.restaurants.forEach((restaurant) => {
        if (restaurant.id !== undefined) {
          restaurant.is_favorite = guestFavorites.includes(Number(restaurant.id))
        }
      })
      searchStore.favoriteStores.forEach((store) => {
        if (store.id !== undefined) {
          store.is_favorite = guestFavorites.includes(Number(store.id))
        }
      })
    } finally {
      searchStore.isSyncingFavorites = false
    }
  }

  // Check if a store is favorited
  const isFavorite = (storeId: number): boolean => {
    const favorites = getCurrentFavorites()
    return favorites.includes(storeId)
  }

  // Update cookie from favorite stores
  const updateCookieFromFavoriteStores = () => {
    writeGuestFavorites(getFavoriteStoreIds(searchStore.favoriteStores))
  }

  // Refactored: Extract authenticated user toggle logic
  const toggleAuthenticatedFavorite = async (
    storeId: number,
    store: FavoriteStore,
    newFavoriteState: boolean
  ): Promise<boolean> => {
    const generation = identityGeneration.current()
    try {
      await sanctumClient('/api/favorites/stores/toggle', {
        method: 'POST',
        body: { store_id: storeId }
      })
      // Every write below belongs to the account that opened the request.
      if (!identityGeneration.isCurrent(generation)) return false

      // Update local state immediately
      searchStore.updateStoreFavorite(storeId, newFavoriteState)

      // Update the favorites list
      applyFavoriteStoreChange(searchStore.favoriteStores, storeId, store, newFavoriteState)

      // Update cookie to maintain synchronization
      updateCookieFromFavoriteStores()

      return true
    } catch (error) {
      if (!identityGeneration.isCurrent(generation)) return false
      logger.error('toggleAuthenticatedFavorite', error)
      return false
    }
  }

  // Extract guest user toggle logic
  const toggleGuestFavorite = (storeId: number, newFavoriteState: boolean): boolean => {
    if (!isValidGuestCookie()) resetGuestFavorites()

    writeGuestFavorites(
      toggleFavoriteId(guestFavoritesCookie.value.favorites, storeId, newFavoriteState)
    )

    // Update local UI
    searchStore.updateStoreFavorite(storeId, newFavoriteState)

    return true
  }

  // Toggle favorite (guest or authenticated)
  const toggleFavorite = async (store: FavoriteStore): Promise<boolean> => {
    const storeId = normalizeFavoriteStoreId(store.id)
    if (storeId === null) {
      return false
    }

    const currentlyFavorite = isAuthenticated.value
      ? (store.is_favorite ?? false)
      : isFavorite(storeId)
    const newFavoriteState = !currentlyFavorite

    if (isAuthenticated.value) {
      return toggleAuthenticatedFavorite(storeId, store, newFavoriteState)
    }

    return toggleGuestFavorite(storeId, newFavoriteState)
  }

  const publishServerFavorites = (generation: number, count: number): SyncResponse => {
    if (!identityGeneration.isCurrent(generation)) return { success: false, count: 0 }
    updateCookieFromFavoriteStores()
    if (!identityGeneration.isCurrent(generation)) return { success: false, count: 0 }
    return { success: true, count }
  }

  // Sync guest favorites to DB (only after login/register)
  const syncGuestFavoritesToDB = async (): Promise<SyncResponse> => {
    const generation = identityGeneration.current()
    if (!isAuthenticated.value) {
      return { success: false, count: 0, errors: ['User not authenticated'] }
    }

    const userStore = useUserStore()
    if (!userStore.hasConsumerAccess) {
      return { success: false, count: 0, errors: ['User does not have consumer access'] }
    }

    // Validate if the cookie belongs to the current guest
    if (!isValidGuestCookie()) {
      // Clean up the old cookie and set a new one for the authenticated user
      resetGuestFavorites()

      // Only load favorites from the server
      if (!identityGeneration.isCurrent(generation)) return { success: false, count: 0 }
      await fetchFavoriteStores()
      return publishServerFavorites(generation, 0)
    }

    const guestFavorites = Normalization.intList(guestFavoritesCookie.value.favorites)

    // First load favorites from server to know if there's anything to sync
    if (searchStore.favoriteStores.length === 0) {
      await fetchFavoriteStores()
    }

    if (!identityGeneration.isCurrent(generation)) return { success: false, count: 0 }
    const serverFavorites = getFavoriteStoreIds(searchStore.favoriteStores)

    // Skip sync if both are empty
    if (guestFavorites.length === 0 && serverFavorites.length === 0) {
      return { success: true, count: 0 }
    }

    isSyncing.value = true

    try {
      if (!identityGeneration.isCurrent(generation)) return { success: false, count: 0 }
      const rawResponse: unknown = await sanctumClient('/api/favorites/stores/sync', {
        method: 'POST',
        body: { store_ids: guestFavorites }
      })
      if (!identityGeneration.isCurrent(generation)) return { success: false, count: 0 }
      const response: FavoritesSyncSuccess = FavoritesSyncSuccessSchema.parse(rawResponse)

      // Reload favorites from DB
      await fetchFavoriteStores()
      return publishServerFavorites(generation, response.data.total_favorites)
    } catch (error: unknown) {
      if (!identityGeneration.isCurrent(generation)) return { success: false, count: 0 }
      logger.error('syncGuestFavoritesToDB', error)
      const problem = AppProblemModel.normalize(error)
      return {
        success: false,
        count: 0,
        get errors() {
          return [
            resolveProblemMessageWithReference(
              problem,
              t,
              t('common.loading.unableToSyncFavorites')
            )
          ]
        }
      }
    } finally {
      if (identityGeneration.isCurrent(generation)) isSyncing.value = false
    }
  }

  // Update cookie from DB (for authenticated users)
  const updateCookieFromDB = async (): Promise<void> => {
    const generation = identityGeneration.current()
    if (!isAuthenticated.value) return

    try {
      // Get favorites from server if not already loaded
      if (searchStore.favoriteStores.length === 0) {
        await fetchFavoriteStores()
      }

      // Update cookie with IDs of favorites from server
      if (!identityGeneration.isCurrent(generation)) return
      updateCookieFromFavoriteStores()
    } catch (error) {
      if (!identityGeneration.isCurrent(generation)) return
      logger.error('updateCookieFromDB', error)
    }
  }

  // Get favorites count
  const getFavoritesCount = (): number => {
    return getCurrentFavorites().length
  }

  return {
    // State
    isSyncing,
    isAuthenticated,

    // Methods
    toggleFavorite,
    isFavorite,
    syncFavoritesFromCookie,
    syncGuestFavoritesToDB,
    updateCookieFromDB,
    getCurrentFavorites,
    getFavoritesCount,
    resetGuestFavorites
  }
}
